import React from "react";

/* Shimmer Skeleton Card */
export function SkeletonCard({ className = "" }) {
  return (
    <div
      className={`border border-white/5 bg-[#07080f]/80 backdrop-blur-xl rounded-2xl p-5 relative overflow-hidden skeleton-shimmer ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="h-3.5 w-24 bg-white/10 rounded-md" />
        <div className="w-8 h-8 rounded-lg bg-white/5" />
      </div>
      <div className="space-y-2">
        <div className="h-8 w-28 bg-white/15 rounded-lg" />
        <div className="h-3 w-36 bg-white/5 rounded-md" />
      </div>
      <div className="pt-4 mt-4 border-t border-white/5 flex items-center justify-between">
        <div className="h-3 w-16 bg-white/5 rounded" />
        <div className="h-3 w-10 bg-primary/20 rounded" />
      </div>
    </div>
  );
}

/* Shimmer Skeleton Table */
export function SkeletonTable({ rows = 6, cols = 5 }) {
  return (
    <div className="border border-white/5 bg-[#07080f]/80 backdrop-blur-xl rounded-2xl overflow-hidden shadow-xl space-y-4 p-5">
      {/* Top Search & Filter toolbar skeleton */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-white/5">
        <div className="h-9 w-64 bg-white/5 rounded-xl border border-white/5" />
        <div className="flex items-center gap-2 overflow-x-auto">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 w-20 bg-white/5 rounded-xl border border-white/5 shrink-0" />
          ))}
        </div>
      </div>

      {/* Table rows */}
      <div className="space-y-3 pt-2">
        {Array.from({ length: rows }).map((_, rIdx) => (
          <div
            key={rIdx}
            className="flex items-center justify-between p-3.5 rounded-xl bg-white/[0.02] border border-white/5 relative overflow-hidden skeleton-shimmer gap-4"
          >
            {/* User/App info */}
            <div className="flex items-center gap-3 min-w-[180px]">
              <div className="w-9 h-9 rounded-full bg-white/10 shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-3.5 w-28 bg-white/15 rounded" />
                <div className="h-2.5 w-36 bg-white/5 rounded" />
              </div>
            </div>

            {/* Secondary fields */}
            <div className="hidden md:block h-3 w-24 bg-white/10 rounded" />
            <div className="hidden sm:block h-5 w-16 bg-white/10 rounded-full" />
            <div className="h-4 w-16 bg-white/10 rounded" />

            {/* Actions */}
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-lg bg-white/5" />
              <div className="w-7 h-7 rounded-lg bg-white/5" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Full Page Admin Skeleton Dashboard */
export default function AdminSkeletonLoader() {
  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Top Header Skeleton */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <div className="h-7 sm:h-9 w-56 sm:w-72 bg-white/15 rounded-xl" />
          <div className="h-3.5 w-64 sm:w-96 bg-white/5 rounded-md" />
        </div>
        <div className="h-10 w-36 bg-primary/20 rounded-xl" />
      </div>

      {/* 4 Metric Cards Skeletons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {[1, 2, 3, 4].map((i) => (
          <SkeletonCard key={i} />
        ))}
      </div>

      {/* Chart & Diagnostics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/5 bg-[#07080f]/80 backdrop-blur-xl rounded-2xl p-6 space-y-6 relative overflow-hidden skeleton-shimmer">
          <div className="flex items-center justify-between pb-4 border-b border-white/5">
            <div className="h-4 w-40 bg-white/15 rounded" />
            <div className="h-4 w-28 bg-white/5 rounded" />
          </div>
          <div className="h-56 w-full rounded-xl bg-white/[0.02] border border-white/5 flex items-end p-4 gap-3">
            {[40, 70, 45, 90, 65, 80, 50].map((h, idx) => (
              <div
                key={idx}
                style={{ height: `${h}%` }}
                className="flex-1 bg-primary/20 rounded-t-md"
              />
            ))}
          </div>
        </div>

        <div className="border border-white/5 bg-[#07080f]/80 backdrop-blur-xl rounded-2xl p-6 space-y-5 relative overflow-hidden skeleton-shimmer">
          <div className="h-4 w-32 bg-white/15 rounded pb-2 border-b border-white/5" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
                <div className="h-3 w-24 bg-white/5 rounded" />
                <div className="h-3 w-20 bg-white/10 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Table Skeleton */}
      <SkeletonTable rows={5} />
    </div>
  );
}
